/**
 * Expande um snippet (Reenvio, Inatividade) em nodes + edges concretos.
 * Operação one-shot — depois de expandida, o snippet vira só registro
 * visual (em graph.snippets) que aponta pros nodes criados.
 */

import { nanoid } from 'nanoid';
import type {
  WorkflowEdge,
  WorkflowNode,
  WorkflowSnippet,
} from '../types';
import { makeNode, newId } from './graph-ops';

export interface SnippetExpansion {
  snippet: WorkflowSnippet;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  /** Primeiro nó da chain — pra conectar com o predecessor */
  entryNodeId: string;
}

/**
 * + Reenvio = Aguardar evento (status terminal de falha) → Envio
 */
export function expandReenvio(originPosition = { x: 0, y: 0 }): SnippetExpansion {
  const eventNode = makeNode(
    'event',
    { x: originPosition.x, y: originPosition.y },
    { onStatuses: ['ORDER_FAILED:CARRIER_REFUSED', 'ORDER_FAILED:CARRIER_CANCELED'] },
  );
  const dispNode = makeNode(
    'disp',
    { x: originPosition.x, y: originPosition.y + 140 },
    { title: 'Reenvio', action: 'CHEAPEST_METHOD' },
  );

  const snippetId = `snippet_${nanoid(6)}`;
  eventNode.snippetId = snippetId;
  dispNode.snippetId = snippetId;

  const edge: WorkflowEdge = {
    id: newId('edge'),
    fromNodeId: eventNode.id,
    fromPortId: 'disparou',
    toNodeId: dispNode.id,
    style: 'fail',
  };

  return {
    snippet: { id: snippetId, kind: 'reenvio', nodeIds: [eventNode.id, dispNode.id], label: 'Reenvio' },
    nodes: [eventNode, dispNode],
    edges: [edge],
    entryNodeId: eventNode.id,
  };
}

/**
 * + Inatividade = Verificar status (timer + status cancelável) → Cancelar → Envio
 */
export function expandInatividade(originPosition = { x: 0, y: 0 }): SnippetExpansion {
  const checkNode = makeNode(
    'check',
    { x: originPosition.x, y: originPosition.y },
    { waitMinutes: 30, carrier: null, stuckInStatuses: [] },
  );
  const cancelNode = makeNode(
    'cancel',
    { x: originPosition.x, y: originPosition.y + 140 },
    { reason: 'Inatividade — pedido preso na transportadora', notifyCarrier: true },
  );
  const dispNode = makeNode(
    'disp',
    { x: originPosition.x, y: originPosition.y + 280 },
    { title: 'Reenvio após cancelamento', action: 'CHEAPEST_METHOD' },
  );

  const snippetId = `snippet_${nanoid(6)}`;
  checkNode.snippetId = snippetId;
  cancelNode.snippetId = snippetId;
  dispNode.snippetId = snippetId;

  const edges: WorkflowEdge[] = [
    {
      id: newId('edge'),
      fromNodeId: checkNode.id,
      fromPortId: 'preso',
      toNodeId: cancelNode.id,
      style: 'alt',
    },
    {
      id: newId('edge'),
      fromNodeId: cancelNode.id,
      fromPortId: 'out',
      toNodeId: dispNode.id,
      style: 'default',
    },
  ];

  return {
    snippet: {
      id: snippetId,
      kind: 'inatividade',
      nodeIds: [checkNode.id, cancelNode.id, dispNode.id],
      label: 'Inatividade',
    },
    nodes: [checkNode, cancelNode, dispNode],
    edges,
    entryNodeId: checkNode.id,
  };
}
